import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getWebhookLogs,
  getWebhookStats,
  retryWebhook,
  testWebhook,
  deleteWebhookLogs,
} from "../../../services/companyAdminService";
import { useApp } from "../../../context/AppContext";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
  Checkbox,
} from "@/components/ui";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import AppLoading from "@/components/AppLoading";
import { DownloadIcon } from "@/components/icons";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReplayIcon from "@mui/icons-material/Replay";
import SendIcon from "@mui/icons-material/Send";

const escapeCsv = (v) => {
  const s = String(v ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const PERIOD_OPTIONS = [7, 30, 90, 0]; // 0 = All

const CompanyWebhooks = () => {
  const { t, isRTL } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [period, setPeriod] = useState(0);
  const [detailsLog, setDetailsLog] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const [testing, setTesting] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const idStr = (id) => (id && typeof id === "object" ? id.toString() : String(id ?? ""));

  const getDateFilter = useCallback(() => {
    if (period === 0) return {};
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - period);
    return { fromDate: fromDate.toISOString() };
  }, [period]);

  const loadWebhookStats = useCallback(async () => {
    const days = period === 0 ? 3650 : period;
    try {
      const data = await getWebhookStats(days);
      setStats(data);
    } catch {
      setStats(null);
    }
  }, [period]);

  const loadWebhookLogs = useCallback(async () => {
    setLoading(true);
    try {
      const dateFilter = getDateFilter();
      const data = await getWebhookLogs({
        limit: 50,
        page,
        status: statusFilter || undefined,
        eventType: eventTypeFilter || undefined,
        ...dateFilter,
      });
      let logs = data?.logs || [];
      if (search) {
        const searchLower = search.toLowerCase();
        logs = logs.filter(
          (log) =>
            (log.eventType || "").toLowerCase().includes(searchLower) ||
            (log.event || "").toLowerCase().includes(searchLower) ||
            (log.contractId?.clientName || "")
              .toLowerCase()
              .includes(searchLower) ||
            (log.contractId?.clientPhone || "").includes(search)
        );
      }
      setWebhookLogs(logs);
      const limitNum = 50;
      setTotalPages(
        (data?.pagination?.pages ?? data?.totalPages ?? Math.ceil((data?.total || 0) / limitNum)) || 1
      );
      setSelectedIds(new Set());
      loadWebhookStats();
      return logs;
    } catch {
      setWebhookLogs([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, eventTypeFilter, search, period, getDateFilter, loadWebhookStats]);

  useEffect(() => {
    loadWebhookLogs();
  }, [loadWebhookLogs]);

  useEffect(() => {
    loadWebhookStats();
  }, [loadWebhookStats]);

  const hasActiveFilters = search || statusFilter || eventTypeFilter || period !== 0;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setEventTypeFilter("");
    setPeriod(0);
    setPage(1);
    navigate(location.pathname, { replace: true });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    const date = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${date} ${time}`;
  };

  const getLogStatus = (log) =>
    log.success || log.status === "success"
      ? isRTL ? "הצלחה" : "Success"
      : isRTL ? "נכשל" : "Failed";

  const getResponseCode = (log) =>
    log.response?.statusCode ?? log.responseCode ?? log.statusCode ?? "-";

  const getAttemptsCount = (log) => {
    if (typeof log.attemptsCount === "number" && log.attemptsCount >= 0) return log.attemptsCount;
    const attempts = log.retry?.attempts;
    if (Array.isArray(attempts)) return attempts.length;
    return log.retry?.attemptNumber ?? 1;
  };

  const getClientDisplay = (log) => {
    const c = log.contractId;
    if (!c) return "-";
    const name = c.clientName || "";
    const phone = c.clientPhone || "";
    return [name, phone].filter(Boolean).join(" · ") || "-";
  };

  const handleRetry = async (log) => {
    if (!log._id) return;
    setRetryingId(log._id);
    const logId = idStr(log._id);
    try {
      await retryWebhook(log._id);
      setSnackbar({
        open: true,
        message: isRTL ? "ה-Webhook נשלח שוב" : "Webhook retry sent",
        severity: "success",
      });
      setWebhookLogs((prev) =>
        prev.map((l) => {
          if (idStr(l._id) !== logId) return l;
          const currentCount = getAttemptsCount(l);
          return { ...l, attemptsCount: currentCount + 1 };
        })
      );
      if (detailsLog && idStr(detailsLog._id) === logId) {
        setDetailsLog((prev) => {
          if (!prev || idStr(prev._id) !== logId) return prev;
          const currentCount = getAttemptsCount(prev);
          return { ...prev, attemptsCount: currentCount + 1 };
        });
      }
      const logs = await loadWebhookLogs();
      await loadWebhookStats();
      if (detailsLog && idStr(detailsLog._id) === logId && Array.isArray(logs)) {
        const fresh = logs.find((l) => idStr(l._id) === logId);
        if (fresh) setDetailsLog(fresh);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || (isRTL ? "שליחה מחדש נכשלה" : "Retry failed"),
        severity: "error",
      });
    } finally {
      setRetryingId(null);
    }
  };

  const handleTestWebhook = async () => {
    setTesting(true);
    try {
      const result = await testWebhook();
      const ok = result?.status === "success";
      setSnackbar({
        open: true,
        message: ok ? t("testWebhookSuccess") : (result?.error || t("testWebhookFailed")),
        severity: ok ? "success" : "warning",
      });
      if (ok) {
        await loadWebhookLogs();
        await loadWebhookStats();
      }
    } catch {
      setSnackbar({
        open: true,
        message: t("testWebhookFailed"),
        severity: "error",
      });
    } finally {
      setTesting(false);
    }
  };

  const buildCsvRows = (logs, options = {}) => {
    const {
      includeClient = true,
      includeResponseTime = true,
      includeError = true,
    } = options;
    const headers = isRTL
      ? ["תאריך", "אירוע", "סטטוס", "קוד תגובה", "ניסיונות"]
      : ["Date", "Event", "Status", "Response Code", "Attempts"];
    if (includeClient) headers.push(isRTL ? "לקוח" : "Client");
    if (includeResponseTime) headers.push(isRTL ? "זמן תגובה (ms)" : "Response time (ms)");
    if (includeError) headers.push(isRTL ? "הודעת שגיאה" : "Error message");

    const rows = logs.map((log) => {
      const row = [
        escapeCsv(formatDate(log.createdAt || log.timestamp)),
        escapeCsv(log.event || log.eventType),
        escapeCsv(getLogStatus(log)),
        escapeCsv(getResponseCode(log)),
        escapeCsv(getAttemptsCount(log)),
      ];
      if (includeClient) row.push(escapeCsv(getClientDisplay(log)));
      if (includeResponseTime)
        row.push(escapeCsv(log.response?.responseTimeMs ?? ""));
      if (includeError) row.push(escapeCsv(log.error?.message ?? ""));
      return row;
    });
    return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  };

  const exportToCsv = (scope = "page") => {
    if (scope === "page") {
      const csv = buildCsvRows(webhookLogs, {
        includeClient: true,
        includeResponseTime: true,
        includeError: true,
      });
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `webhooks-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }
    setExportingAll(true);
    getWebhookLogs({
      limit: 2000,
      page: 1,
      status: statusFilter || undefined,
      eventType: eventTypeFilter || undefined,
      ...getDateFilter(),
    })
      .then((data) => {
        let logs = data?.logs || [];
        if (search) {
          const searchLower = search.toLowerCase();
          logs = logs.filter(
            (log) =>
              (log.eventType || "").toLowerCase().includes(searchLower) ||
              (log.event || "").toLowerCase().includes(searchLower) ||
              (log.contractId?.clientName || "")
                .toLowerCase()
                .includes(searchLower) ||
              (log.contractId?.clientPhone || "").includes(search)
          );
        }
        const csv = buildCsvRows(logs, {
          includeClient: true,
          includeResponseTime: true,
          includeError: true,
        });
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `webhooks-all-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        setSnackbar({
          open: true,
          message: isRTL ? `יוצאו ${logs.length} רשומות` : `Exported ${logs.length} rows`,
          severity: "success",
        });
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: isRTL ? "ייצוא נכשל" : "Export failed",
          severity: "error",
        });
      })
      .finally(() => setExportingAll(false));
  };

  const isFailedOrRetrying = (log) =>
    log.status === "failed" || log.status === "retrying";

  const toggleSelect = (logId) => {
    const sid = idStr(logId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (webhookLogs.length === 0) return;
    if (selectedIds.size === webhookLogs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(webhookLogs.map((l) => idStr(l._id))));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const msg = isRTL
      ? `למחוק ${selectedIds.size} רשומות לוג? פעולה זו אינה הפיכה.`
      : `Delete ${selectedIds.size} log entries? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBulkLoading(true);
    try {
      const ids = [...selectedIds];
      await deleteWebhookLogs(ids);
      setSelectedIds(new Set());
      const logs = await loadWebhookLogs();
      if (detailsLog && ids.includes(idStr(detailsLog._id))) setDetailsLog(null);
      loadWebhookStats();
      setSnackbar({
        open: true,
        message: isRTL ? `נמחקו ${ids.length} רשומות` : `${ids.length} entries deleted`,
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || (isRTL ? "מחיקה נכשלה" : "Delete failed"),
        severity: "error",
      });
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <Box sx={{ pt: 2 }}>
      {/* Row 1: Title + actions (Send test, Export current page, Export all) */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" fontWeight={600}>
          Webhook {isRTL ? "לוגים" : "Logs"}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            onClick={handleTestWebhook}
            disabled={testing}
            startIcon={<SendIcon sx={{ width: 18, height: 18 }} />}
          >
            {t("sendTestFromPage")}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => exportToCsv("page")}
            disabled={webhookLogs.length === 0}
            startIcon={
              <Box component="span" sx={{ display: "inline-flex", "& svg": { width: 18, height: 18 } }}>
                <DownloadIcon />
              </Box>
            }
          >
            {t("exportCurrentPage")}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() => exportToCsv("all")}
            disabled={exportingAll}
            sx={{ color: "#fff" }}
            startIcon={
              <Box component="span" sx={{ display: "inline-flex", "& svg": { width: 18, height: 18 } }}>
                <DownloadIcon />
              </Box>
            }
          >
            {t("exportAllFiltered")}
          </Button>
        </Stack>
      </Stack>

      {/* KPI row: full width, boxes share width equally + one extra metric */}
      {stats && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          <Card variant="outlined" sx={{ flex: "1 1 0", minWidth: 120, maxWidth: "100%" }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("webhookStatsTotal")}
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {stats.total ?? 0}
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: "1 1 0", minWidth: 120, maxWidth: "100%" }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("webhookStatsSuccessRate")}
              </Typography>
              <Typography variant="h5" fontWeight={600} color="success.main">
                {stats.successRate ?? 0}%
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: "1 1 0", minWidth: 120, maxWidth: "100%" }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("webhookStatsSuccess")}
              </Typography>
              <Typography variant="h5" fontWeight={600} color="success.main">
                {stats.success ?? 0}
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: "1 1 0", minWidth: 120, maxWidth: "100%" }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("webhookStatsAvgTime")}
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {stats.avgResponseTimeMs ?? 0} ms
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: "1 1 0", minWidth: 120, maxWidth: "100%" }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("webhookStatsFailed")}
              </Typography>
              <Typography variant="h5" fontWeight={600} color="error.main">
                {(stats.failed ?? 0) + (stats.retrying ?? 0)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {isRTL ? "הגדרות Webhook – להגדרת Webhook URL, " : "Webhook Configuration – Configure your Webhook URL in Settings. "}
        <Typography
          component="button"
          variant="body2"
          onClick={() => navigate("/company/settings")}
          sx={{
            border: "none",
            background: "none",
            padding: 0,
            marginInlineStart: 0.5,
            cursor: "pointer",
            color: "primary.main",
            textDecoration: "underline",
            font: "inherit",
            fontSize: "inherit",
            "&:hover": { color: "primary.light" },
          }}
        >
          {isRTL ? "עבור להגדרות" : "Go to Settings"}
        </Typography>
      </Typography>

      {/* Filters */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1.5 }}
      >
        <TextField
          size="small"
          placeholder={
            isRTL ? "חיפוש לפי אירוע או לקוח..." : "Search by event or client..."
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{isRTL ? "סטטוס" : "Status"}</InputLabel>
          <Select
            value={statusFilter}
            label={isRTL ? "סטטוס" : "Status"}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">{isRTL ? "כל הסטטוסים" : "All"}</MenuItem>
            <MenuItem value="success">{isRTL ? "הצלחה" : "Success"}</MenuItem>
            <MenuItem value="failed">{isRTL ? "נכשל" : "Failed"}</MenuItem>
            <MenuItem value="pending">{isRTL ? "ממתין" : "Pending"}</MenuItem>
            <MenuItem value="retrying">{isRTL ? "מנסה שוב" : "Retrying"}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{isRTL ? "אירוע" : "Event"}</InputLabel>
          <Select
            value={eventTypeFilter}
            label={isRTL ? "אירוע" : "Event"}
            onChange={(e) => setEventTypeFilter(e.target.value)}
          >
            <MenuItem value="">{isRTL ? "כל האירועים" : "All"}</MenuItem>
            <MenuItem value="contract.created">{isRTL ? "חוזה נוצר" : "Contract Created"}</MenuItem>
            <MenuItem value="contract.viewed">{isRTL ? "חוזה נצפה" : "Contract Viewed"}</MenuItem>
            <MenuItem value="contract.signed">{isRTL ? "חוזה נחתם" : "Contract Signed"}</MenuItem>
            <MenuItem value="contract.expired">{isRTL ? "חוזה פג תוקף" : "Contract Expired"}</MenuItem>
            <MenuItem value="contract.cancelled">{isRTL ? "חוזה בוטל" : "Contract Cancelled"}</MenuItem>
            <MenuItem value="test.ping">{isRTL ? "בדיקה" : "Test Ping"}</MenuItem>
          </Select>
        </FormControl>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(e, v) => v != null && setPeriod(v)}
          size="small"
          aria-label="period"
          sx={{
            "& .MuiToggleButton-root": { px: 1.75 },
            ...(isRTL && {
              flexDirection: "row-reverse",
              "& .MuiToggleButtonGroup-grouped": { flexDirection: "row-reverse" },
            }),
          }}
        >
          {(isRTL ? [...PERIOD_OPTIONS].reverse() : PERIOD_OPTIONS).map((d) => (
            <ToggleButton
              key={d}
              value={d}
              aria-label={d === 0 ? (isRTL ? "הכל" : "All") : (isRTL ? `ימים ${d}` : `${d} days`)}
              sx={isRTL ? { direction: "rtl" } : undefined}
            >
              {d === 0 ? (isRTL ? "הכל" : "All") : (isRTL ? `ימים ${d}` : `${d} days`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {hasActiveFilters && (
          <Button size="small" variant="outlined" onClick={clearFilters} sx={{ minWidth: "auto" }}>
            {isRTL ? "נקה סינון" : "Clear filter"}
          </Button>
        )}
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
            <Typography variant="h6">
              {isRTL ? "לוג אירועים אחרון" : "Recent Events Log"}
            </Typography>
            {period !== 0 && (
              <Typography variant="body2" color="text.secondary">
                {isRTL
                  ? `מציג לוגים מ-${period} ימים אחרונים בלבד • בחר "הכל" לראות את כולם`
                  : `Showing logs from last ${period} days only • Select "All" to see all`}
              </Typography>
            )}
          </Stack>

          {selectedIds.size > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="body2" fontWeight={500}>
                {isRTL
                  ? `${selectedIds.size} רשומות נבחרו`
                  : `${selectedIds.size} selected`}
              </Typography>
              <Button
                size="small"
                color="error"
                variant="contained"
                onClick={handleBulkDelete}
                disabled={bulkLoading}
                sx={{ minWidth: "auto", px: 1, py: 0.25, fontSize: "0.75rem" }}
              >
                {bulkLoading
                  ? "..."
                  : isRTL
                    ? `מחיקה (${selectedIds.size})`
                    : `Delete (${selectedIds.size})`}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedIds(new Set())}
                disabled={bulkLoading}
                sx={{ minWidth: "auto", px: 1, py: 0.25, fontSize: "0.75rem" }}
              >
                {isRTL ? "בטל בחירה" : "Clear"}
              </Button>
            </Stack>
          )}

          {loading ? (
            <AppLoading fullPage />
          ) : webhookLogs.length === 0 ? (
            <Box py={6} textAlign="center">
              <Typography color="text.secondary" gutterBottom>
                {isRTL ? "אין לוגים" : "No Logs"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isRTL
                  ? "אירועי webhook יופיעו כאן לאחר שיישלחו"
                  : "Webhook events will appear here after they are sent"}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={webhookLogs.length > 0 && selectedIds.size === webhookLogs.length}
                          indeterminate={selectedIds.size > 0 && selectedIds.size < webhookLogs.length}
                          onChange={toggleSelectAll}
                        />
                      </TableCell>
                      <TableCell>{isRTL ? "תאריך" : "Date"}</TableCell>
                      <TableCell>{isRTL ? "אירוע" : "Event"}</TableCell>
                      <TableCell>{isRTL ? "סטטוס" : "Status"}</TableCell>
                      <TableCell>{isRTL ? "קוד תגובה" : "Response"}</TableCell>
                      <TableCell>{isRTL ? "ניסיונות" : "Attempts"}</TableCell>
                      <TableCell align="center" width={120}>
                        {isRTL ? "פעולות" : "Actions"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {webhookLogs.map((log, index) => (
                      <TableRow
                        key={log._id || index}
                        selected={selectedIds.has(idStr(log._id))}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => setDetailsLog(log)}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(idStr(log._id))}
                            onChange={() => toggleSelect(log._id)}
                          />
                        </TableCell>
                        <TableCell>{formatDate(log.createdAt || log.timestamp)}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", direction: "ltr" }}>
                          {log.event || log.eventType}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getLogStatus(log)}
                            size="small"
                            color={
                              log.success || log.status === "success" ? "success" : "error"
                            }
                            sx={{ color: "#fff" }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {getResponseCode(log)}
                        </TableCell>
                        <TableCell>{getAttemptsCount(log)}</TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            onClick={() => setDetailsLog(log)}
                            title={t("viewDetails")}
                            aria-label={t("viewDetails")}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          {isFailedOrRetrying(log) && (
                            <IconButton
                              size="small"
                              onClick={() => handleRetry(log)}
                              disabled={retryingId === log._id}
                              title={t("retry")}
                              aria-label={t("retry")}
                              color="primary"
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalPages > 1 && (
                <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, p) => setPage(p)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Log details dialog */}
      <Dialog
        open={!!detailsLog}
        onClose={() => setDetailsLog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { direction: isRTL ? "rtl" : "ltr" } }}
      >
        {detailsLog && (
          <>
            <DialogTitle>{t("logDetails")}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isRTL ? "אירוע" : "Event"}
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {detailsLog.event || detailsLog.eventType}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isRTL ? "תאריך" : "Date"}
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(detailsLog.createdAt || detailsLog.timestamp)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    URL
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {detailsLog.webhookUrl || "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isRTL ? "סטטוס" : "Status"}
                  </Typography>
                  <Typography variant="body2">{getLogStatus(detailsLog)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isRTL ? "קוד תגובה" : "Response code"}
                  </Typography>
                  <Typography variant="body2">{getResponseCode(detailsLog)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("responseTime")}
                  </Typography>
                  <Typography variant="body2">
                    {detailsLog.response?.responseTimeMs != null
                      ? `${detailsLog.response.responseTimeMs} ms`
                      : "-"}
                  </Typography>
                </Box>
                {detailsLog.contractId && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("webhookClient")}
                    </Typography>
                    <Typography variant="body2">{getClientDisplay(detailsLog)}</Typography>
                    <Button
                      size="small"
                      sx={{ mt: 0.5 }}
                      onClick={() => {
                        navigate(`/company/contracts?contract=${detailsLog.contractId?._id || detailsLog.contractId}`);
                        setDetailsLog(null);
                      }}
                    >
                      {t("contractLink")}
                    </Button>
                  </Box>
                )}
                {detailsLog.error?.message && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("errorMessage")}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        bgcolor: "action.hover",
                        p: 1,
                        borderRadius: 1,
                      }}
                    >
                      {detailsLog.error.message}
                    </Typography>
                  </Box>
                )}
                {detailsLog.response?.body != null && detailsLog.response.body !== "" && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {isRTL ? "גוף תגובה" : "Response body"}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: 120,
                        overflow: "auto",
                        bgcolor: "action.hover",
                        p: 1,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                      }}
                    >
                      {typeof detailsLog.response.body === "string"
                        ? detailsLog.response.body
                        : JSON.stringify(detailsLog.response.body)}
                    </Typography>
                  </Box>
                )}
                {detailsLog.retry?.attempts?.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("attemptsHistory")}
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {detailsLog.retry.attempts.map((a, i) => (
                        <Typography key={i} variant="body2" component="div">
                          #{a.attemptNumber}:{" "}
                          {a.attemptedAt ? formatDate(a.attemptedAt) : "-"} →{" "}
                          {a.statusCode ?? "-"} {a.responseTimeMs != null ? `(${a.responseTimeMs} ms)` : ""}{" "}
                          {a.success ? "✓" : a.error ? `- ${a.error}` : ""}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              {isFailedOrRetrying(detailsLog) && (
                <Button
                  onClick={() => handleRetry(detailsLog)}
                  disabled={retryingId === detailsLog._id}
                  startIcon={<ReplayIcon />}
                >
                  {t("retry")}
                </Button>
              )}
              <Button onClick={() => setDetailsLog(null)}>{t("close")}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: isRTL ? "left" : "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          dir={isRTL ? "rtl" : "ltr"}
          sx={{
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
            "& .MuiAlert-action .MuiIconButton-root": { color: "#fff" },
            "& .MuiAlert-action .MuiIconButton-root:hover": {
              backgroundColor: "rgba(255,255,255,0.2)",
            },
            ...(isRTL && {
              "& .MuiAlert-message": {
                paddingInlineStart: 2,
                paddingInlineEnd: 2.5,
              },
              "& .MuiAlert-action": { marginInlineStart: 1 },
            }),
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyWebhooks;
